import { formatCurrency, getUserLocale } from '@monorepo/client-common';
import { Theme } from '@mui/material';
import { Currency, Locale, StatisticValueType, Trend } from '../types';

export type FormatStatisticOptions = {
  valueType?: StatisticValueType;
  decimalPlaces?: number;
  showDecimalsIfPresent?: boolean;
  maxDecimalPlaces?: number;
  currency?: Currency;
  locale?: Locale;
};

export const toFiniteNumber = (
  value: string | number | undefined | null,
  fallback = 0,
): number => {
  if (value === undefined || value === null) return fallback;
  const num = typeof value === 'number' ? value : parseFloat(String(value));
  return Number.isFinite(num) ? num : fallback;
};

const getIntlLocale = (locale?: string) => {
  if (!locale) return getUserLocale();

  const normalized = locale.toLowerCase();
  const [lang, region] = normalized.split(/[-_]/);

  if (lang === 'en' && region === 'us') return 'en';
  if (lang === 'hi') return 'hi';
  if (region === 'in') return 'en-in';
  if (lang === 'es') return 'es';
  if (lang === 'pt') return 'pt';
  if (lang === 'ja') return 'ja';

  return 'en-gb';
};

export const formatStatisticValue = (
  value: number | undefined,
  options: FormatStatisticOptions = {},
): string => {
  if (value === undefined) {
    return '';
  }

  const {
    valueType = 'number',
    showDecimalsIfPresent = false,
    maxDecimalPlaces,
    currency = 'USD',
    locale,
  } = options;
  const rawDecimalPlaces = options.decimalPlaces ?? 2;
  const decimalPlaces = Math.max(0, rawDecimalPlaces);

  const intlLocale = getIntlLocale(locale);

  const currencyOptions = {
    decimalPlaces,
    showDecimalsIfPresent,
    locale: intlLocale,
    ...(showDecimalsIfPresent && {
      maxDecimalPlaces: Math.max(0, maxDecimalPlaces ?? decimalPlaces),
    }),
  };

  switch (valueType) {
    case 'currency': {
      const formatted = formatCurrency(value, {
        currency,
        ...currencyOptions,
      });
      // Add space between currency symbol and value (e.g., "US$160" -> "US$ 160")
      return formatted.replace(/^([^\d-]+)([-\d])/, '$1 $2');
    }

    case 'percent': {
      const formatted = formatCurrency(value, currencyOptions);
      return `${formatted}%`;
    }

    default:
      return formatCurrency(value, currencyOptions);
  }
};

export const getTrendDeltaValue = (values: number[]): number => {
  if (!values || values.length === 0) return 0;
  const firstValue = values[0];
  const lastValue = values[values.length - 1];
  return parseFloat((lastValue - firstValue).toFixed(10));
};

export const getTrendDirection = (
  deltaValue: number,
  threshold: number,
): Trend => {
  if (deltaValue > threshold) return 'positive';
  if (deltaValue < -threshold) return 'negative';
  return 'neutral';
};

export const getTrendColor = (
  theme: Theme,
  trend?: Trend,
  error?: boolean,
): string => {
  if (error) return theme.palette.error.main;
  if (trend === 'positive') return theme.palette.success.main;
  if (trend === 'negative') return theme.palette.error.main;
  return theme.palette.text.primary;
};

export const getChartColor = (theme: Theme, trend: Trend): string => {
  if (trend === 'positive') return theme.palette.success.main;
  if (trend === 'negative') return theme.palette.error.main;
  return theme.palette.primary.main;
};

export interface CardSxOptions {
  clickable?: boolean;
  disabled?: boolean;
}

export const getCardSx = ({ clickable, disabled }: CardSxOptions = {}) => ({
  width: 'fit-content',
  borderRadius: (theme: Theme) => `${theme.customShape.borderRadius3XL}px`,
  borderColor: 'text.fifth',
  backgroundColor: 'background.default',
  ...(disabled && {
    cursor: 'not-allowed',
    opacity: 0.5,
  }),
  ...(clickable &&
    !disabled && {
      cursor: 'pointer',
      transition: (theme: Theme) =>
        theme.transitions.create(['background-color', 'box-shadow']),
      '&:hover': {
        backgroundColor: 'action.hover',
      },
    }),
});
